import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const inputCls = 'h-9 dark:bg-slate-900 dark:border-slate-600';

function YesNo({ label, checked, onChange, testId, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid={`${testId}-card`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{label}</span>
        <div className="inline-flex rounded-md overflow-hidden border border-slate-300 dark:border-slate-600 text-xs">
          <button type="button" onClick={() => onChange(true)} data-testid={`${testId}-yes`} aria-pressed={checked}
            className={`px-3 py-1 ${checked ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'}`}>Yes</button>
          <button type="button" onClick={() => onChange(false)} data-testid={`${testId}-no`} aria-pressed={!checked}
            className={`px-3 py-1 ${!checked ? 'bg-rose-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'}`}>No</button>
        </div>
      </div>
      {checked && children}
    </div>
  );
}

// EPF / SOCSO / EIS / PCB / HRDF / SKBKK applicability + rates. Defaults auto-apply when nationality changes.
export function StatutoryContributionsForm({ value, onChange, nationality, icNumber, wages }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const seq = useRef(0);

  // Parents reset `value` to null when nationality changes, so an empty value means "load defaults".
  // Legacy records (saved before the LINDUNG 24 Jam engine) have no skbkk_mode — let the defaults decide SKBKK.
  useEffect(() => {
    const legacy = !!value && !('skbkk_mode' in value);
    const empty = !value || Object.keys(value).length === 0;
    if (!empty && !legacy) return;
    const mine = ++seq.current;
    axios.get(`${API}/statutory/defaults`, { params: { nationality: nationality || 'malaysian', ic_number: icNumber || undefined } })
      .then((r) => {
        if (mine !== seq.current) return; // a newer value/nationality superseded this request
        const keep = { ...(value || {}) };
        if (legacy) ['skbkk_applicable', 'skbkk_employee_amount', 'skbkk_employer_amount'].forEach((k) => delete keep[k]);
        onChange({ ...r.data, ...keep });
      })
      .catch(() => {});
  }, [nationality, value]);

  const rateInput = (key, placeholder, testId) => (
    <Input type="number" step="0.5" min="0" max="100" value={v[key] ?? ''} placeholder={placeholder}
      onChange={(e) => set({ [key]: e.target.value === '' ? null : parseFloat(e.target.value) })}
      data-testid={testId} className={inputCls} />
  );

  return (
    <div className="space-y-3" data-testid="statutory-contributions-form">
      <p className="text-xs text-slate-500 dark:text-slate-400">Defaults are set from nationality (and age from NRIC). Change any value to override for this employee.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <YesNo label="EPF (KWSP)" checked={v.epf_applicable !== false} onChange={(b) => set({ epf_applicable: b })} testId="stat-epf">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Employee rate % <span className="text-slate-400">(blank = auto 11%)</span></Label>{rateInput('epf_employee_rate', 'auto', 'stat-epf-employee-rate')}</div>
            <div><Label className="text-xs">Employer rate % <span className="text-slate-400">(blank = auto 13/12%)</span></Label>{rateInput('epf_employer_rate', 'auto', 'stat-epf-employer-rate')}</div>
          </div>
        </YesNo>
        <YesNo label="SOCSO (PERKESO)" checked={v.socso_applicable !== false} onChange={(b) => set({ socso_applicable: b })} testId="stat-socso">
          <div>
            <Label className="text-xs">Category</Label>
            <select value={v.socso_category ?? 1} onChange={(e) => set({ socso_category: parseInt(e.target.value, 10) })} data-testid="stat-socso-category"
              className="w-full h-9 rounded-md border border-slate-300 dark:border-slate-600 px-2 text-sm bg-white dark:bg-slate-900 dark:text-slate-100">
              <option value={1}>Category 1 — Employment Injury + Invalidity (employee & employer)</option>
              <option value={2}>Category 2 — Employment Injury only (employer only; age 60+ / foreign)</option>
            </select>
          </div>
        </YesNo>
        <YesNo label="EIS (SIP)" checked={v.eis_applicable !== false} onChange={(b) => set({ eis_applicable: b })} testId="stat-eis">
          <p className="text-xs text-slate-500">0.2% employee + 0.2% employer (capped at RM 6,000 wages)</p>
        </YesNo>
        <YesNo label="PCB / MTD (LHDN)" checked={v.pcb_applicable !== false} onChange={(b) => set({ pcb_applicable: b })} testId="stat-pcb">
          <p className="text-xs text-slate-500">Monthly tax deduction computed from the Tax Relief declarations below</p>
        </YesNo>
        <YesNo label="HRDF Levy (employer)" checked={!!v.hrdf_applicable} onChange={(b) => set({ hrdf_applicable: b })} testId="stat-hrdf">
          <p className="text-xs text-slate-500">1% of monthly wages, employer only</p>
        </YesNo>
        <SkbkkCard v={v} set={set} nationality={nationality} wages={wages} />
      </div>
    </div>
  );
}

const fmtRM = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}`;

// SKBKK / LINDUNG 24 Jam (PERKESO): mandatory for foreign workers, opt-in for locals; auto = phased % × wages ≤ RM 6,000.
function SkbkkCard({ v, set, nationality, wages }) {
  const [preview, setPreview] = useState(null);
  const mandatory = nationality === 'non_malaysian_temporary';
  const enrolled = mandatory || !!v.skbkk_applicable;
  const mode = v.skbkk_mode || 'auto';

  useEffect(() => {
    if (!enrolled || mode !== 'auto') return;
    const t = setTimeout(() => {
      axios.get(`${API}/statutory/skbkk-preview`, { params: { wages: Number(wages) || 0, nationality: nationality || 'malaysian' } })
        .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(t);
  }, [wages, nationality, enrolled, mode]);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2" data-testid="stat-skbkk-card">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">SKBKK — LINDUNG 24 Jam (PERKESO)</span>
        {mandatory ? (
          <span className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" data-testid="stat-skbkk-mandatory">Mandatory — foreign worker</span>
        ) : (
          <div className="inline-flex rounded-md overflow-hidden border border-slate-300 dark:border-slate-600 text-xs">
            <button type="button" onClick={() => set({ skbkk_applicable: true })} data-testid="stat-skbkk-yes" aria-pressed={enrolled}
              className={`px-3 py-1 ${enrolled ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'}`}>Enrolled</button>
            <button type="button" onClick={() => set({ skbkk_applicable: false })} data-testid="stat-skbkk-no" aria-pressed={!enrolled}
              className={`px-3 py-1 ${!enrolled ? 'bg-rose-600 text-white' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'}`}>Opted out</button>
          </div>
        )}
      </div>
      {enrolled ? (
        <>
          <div className="flex items-center gap-3 text-xs">
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" name="skbkk_mode" checked={mode === 'auto'} onChange={() => set({ skbkk_mode: 'auto' })} data-testid="stat-skbkk-mode-auto" /> Auto (PERKESO rate)
            </label>
            <label className="inline-flex items-center gap-1 cursor-pointer">
              <input type="radio" name="skbkk_mode" checked={mode === 'manual'} onChange={() => set({ skbkk_mode: 'manual' })} data-testid="stat-skbkk-mode-manual" /> Manual amount
            </label>
          </div>
          {mode === 'auto' ? (
            <p className="text-xs text-slate-600 dark:text-slate-300" data-testid="stat-skbkk-preview">
              {preview ? (
                <>Employee <strong>{preview.rate}%</strong> × wages (capped {fmtRM(preview.wage_ceiling)}) → <strong>{fmtRM(preview.employee)}</strong>/month on {fmtRM(preview.wage_base)}. Employer share: nil. Rate rises to 1.00% from Jun 2028 and 1.25% from Jun 2031.</>
              ) : 'Computed each payroll run: 0.75% of monthly wages (capped at RM 6,000), fully borne by the employee.'}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Employee (RM / month)</Label>
                <Input type="number" step="0.01" min="0" value={v.skbkk_employee_amount ?? ''} onChange={(e) => set({ skbkk_employee_amount: parseFloat(e.target.value) || 0 })} data-testid="stat-skbkk-employee" className={inputCls} /></div>
              <div><Label className="text-xs">Employer (RM / month)</Label>
                <Input type="number" step="0.01" min="0" value={v.skbkk_employer_amount ?? ''} onChange={(e) => set({ skbkk_employer_amount: parseFloat(e.target.value) || 0 })} data-testid="stat-skbkk-employer" className={inputCls} /></div>
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-slate-500">Employee submitted the PERKESO opt-out (Notis Pelepasan Liabiliti). No deduction will be made.</p>
      )}
    </div>
  );
}
